import { useState } from 'react';
import { Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useTranslation } from 'react-i18next';
import { DarwinButton } from 'components/darwin-ui';
import { useUploadMassiveLoadFile } from 'api/services/dataCaptureService';
import { ErrorType } from 'types/genericTypes';
import Icon from 'components/darwin-ui/Icon';

export const UploadFile = ({
  traceabilityPlanId,
  formId,
  processMapName,
  formName,
  processName,
  environment,
  dataSourceId,
}: {
  traceabilityPlanId: string;
  formId: string;
  processMapName: string;
  formName: string;
  processName: string;
  environment: string;
  dataSourceId: string;
}) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { mutateAsync: uploadFile } = useUploadMassiveLoadFile();

  const beforeUpload = (file: RcFile) => {
    const isAllowedType = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
    ].includes(file.type);
    if (!isAllowedType) {
      message.error(t('process-flow.data-capture.upload-file-extension-error'));
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const uploadProps: UploadProps = {
    name: 'file',
    maxCount: 1,
    beforeUpload,
    customRequest: async ({ file, onSuccess, onError }) => {
      setIsUploading(true);
      try {
        await uploadFile({
          file: file as File,
          traceabilityPlanId,
          formId,
          processMapName,
          formName,
          processName: processName.toUpperCase().replaceAll(' ', '_'),
          environment,
          dataSourceId,
        });
        message.success(t('process-flow.data-capture.upload-file-success'));
        onSuccess?.({ success: true });
      } catch (err) {
        const apiError = err as ErrorType;
        const hasErrorCode = Boolean(apiError.body?.errorCode);
        const missingHeaders = apiError.body?.metadata
          ?.missingHeaders as string[];

        // If missing headers, show a combined message with all missing headers
        if (hasErrorCode && missingHeaders?.length) {
          const errorMessage =
            missingHeaders.length > 1
              ? t(
                  'process-flow.data-capture.upload-file-missing-headers-error',
                  { headers: missingHeaders.join(', ') }
                )
              : t(
                  'process-flow.data-capture.upload-file-missing-header-error',
                  { header: missingHeaders[0] }
                );

          message.error(errorMessage);
          onError?.(new Error(errorMessage));
          setIsUploading(false);
          return;
        }

        const errorMessage = hasErrorCode
          ? t(apiError.body.errorCode)
          : t('process-flow.data-capture.upload-file-error');

        message.error(errorMessage);
        onError?.(new Error(errorMessage));
      } finally {
        setIsUploading(false);
      }
    },
  };

  return (
    <Upload {...uploadProps}>
      <DarwinButton
        disabled={isUploading}
        type="primary"
        icon={<Icon name="upload-file" size="lg" />}
      >
        {t('process-flow.data-capture.upload-file')}
      </DarwinButton>
    </Upload>
  );
};
